import apiClient from "./apiClient";
import { ImportBnsfRoadDisputesResult, PreviewBnsfRoadDisputesResult } from "finance/bnsfRoadDisputeModels";

export interface ImportBnsfFuelRatesResult {
  parsedRowCount: number;
  insertedCount: number;
}

/**
 * Uploads Heidi's BNSF fuel rate spreadsheet. The API parses columns B-E, resolves each
 * VanBaseID to a hub, and inserts the rates against the given effective date.
 * @param effectiveDate ISO date string (yyyy-MM-dd).
 */
const importBnsfFuelRates = async (file: File, effectiveDate: string): Promise<ImportBnsfFuelRatesResult> => {
  const formData = new FormData();
  formData.append("effectiveDate", effectiveDate);
  formData.append("file", file);
  return apiClient.post<FormData, ImportBnsfFuelRatesResult>("/finance/fuel-rates/bnsf/import", formData);
};

export interface PayrollDryRunPunch {
  punchId: string;
  punchType: string;
  paycomPunchType: string;
  punchTimeUtc: string;
  paycomUnixTime: number;
  timeZone?: string;
  paycomTimeZone?: string;
  punchDesc?: string;
}

export interface PayrollDryRunGroup {
  type: "Trip" | "Shift";
  id: number;
  employeeCode: string;
  employeeName?: string;
  hub?: string;
  punches: PayrollDryRunPunch[];
  /** First In-Day punch (UTC); absent when the punches have no clock-in. */
  clockInUtc?: string;
  /** Last Out-Day punch (UTC); absent when the punches have no clock-out. */
  clockOutUtc?: string;
  /** Unpaid break minutes remaining after the free-break transform. */
  unpaidBreakMinutes: number;
  /** Clock-out minus clock-in minus unpaid breaks, in hours; absent when in/out is missing. */
  paidHours?: number;
}

export interface PayrollDryRunSummary {
  totalPunches: number;
  tripCount: number;
  shiftCount: number;
  employeeCount: number;
  duplicatesRemoved: number;
  earliestPunchUtc?: string;
  latestPunchUtc?: string;
  approvalHoursRequired: number;
  maxDaysToQuery?: number;
  startDate?: string;
  endDate?: string;
}

export interface PayrollDryRunResult {
  generatedUtc: string;
  summary: PayrollDryRunSummary;
  groups: PayrollDryRunGroup[];
  warnings: string[];
}

/**
 * Read-only preview of the payroll time punch run — what the payroll function app
 * would send to Paycom if it ran now. Nothing is written or published.
 * Pass no dates to mirror the scheduled job, or both dates (yyyy-MM-dd) to mirror
 * the date-range trigger.
 */
const runPayrollDryRun = async (start?: string, end?: string): Promise<PayrollDryRunResult> =>
  apiClient.get<{ start?: string; end?: string }, PayrollDryRunResult>("/finance/payroll/dry-run", {
    start,
    end,
  });

export interface PayrollTripStatus {
  tripId: number;
  status: string;
  detail?: string;
  employeeCode?: string;
  employeeName?: string;
  hub?: string;
  pickupDateTime?: string;
  approved?: boolean;
  approvedDateTime?: string;
  hasTimePunchLog: boolean;
  retryPublish: boolean;
  queuedCount: number;
  publishedCount: number;
  failedCount: number;
  lastActivityUtc?: string;
  lastFailureMessage?: string;
  /** Punches the payroll logic computes for this trip right now — what SHOULD be sent. */
  punches?: PayrollDryRunPunch[];
  /** First In-Day punch (UTC) of the computed punches; absent when there is no clock-in. */
  clockInUtc?: string;
  /** Last Out-Day punch (UTC) of the computed punches; absent when there is no clock-out. */
  clockOutUtc?: string;
  /** Unpaid break minutes remaining after the free-break transform. */
  unpaidBreakMinutes: number;
  /** Clock-out minus clock-in minus unpaid breaks, in hours; absent when in/out is missing. */
  paidHours?: number;
  /** Transform warnings for this trip (missing start/end, unbalanced breaks, ...). */
  warnings?: string[];
}

/**
 * Read-only payroll pipeline status for specific trips: published, failed,
 * locked/stuck, waiting on approval delay, or eligible for the next run.
 */
const getPayrollTripStatuses = async (tripIds: number[]): Promise<PayrollTripStatus[]> =>
  apiClient.get<{ tripIds: number[] }, PayrollTripStatus[]>("/finance/payroll/trip-status", { tripIds });

export interface BnsfFollowUpsSheetSummary {
  name: string;
  rowCount: number;
  skipped: boolean;
}

export interface BnsfFollowUpsRunOptions {
  /** "GPS van check" -- default ON. */
  includeVanCheck: boolean;
  /** "Reopen change notes" -- default OFF ("slow: ~1 min per 10 rows"). */
  includeReopenNotes: boolean;
}

export interface BnsfFollowUpsStartResult {
  runId: string;
  totalRows: number;
  batchCount: number;
  sheets: BnsfFollowUpsSheetSummary[];
  /** Unique vouchers -- the "N" in "N vouchers · M van-conflict rows · ~X min" (totalRows counts data rows, not unique vouchers). */
  voucherCount: number;
  /** Unique vouchers whose comment names a peer GUID -- the "M van-conflict rows" half of the estimate. */
  vanConflictRowCount: number;
  /** The "~X min" up-front estimate, at the BFF's current (rolling-average) timing constants. */
  estimatedSeconds: number;
}

export interface BnsfFollowUpsBatchResult {
  processedRows: number;
  totalRows: number;
  isComplete: boolean;
  batchNumber: number;
  batchCount: number;
  /** The slowest step OrderApi reported for this one batch (e.g. "GPS van check") -- absent once complete. */
  currentStep?: string;
  /** "about N min left", refined from this run's own batch times so far -- absent once complete. */
  estimatedSecondsRemaining?: number;
}

export type BnsfFollowUpsBatchOutcome =
  | ({ status: "ok" } & BnsfFollowUpsBatchResult)
  | { status: "expired" }
  | { status: "failed"; failedStep: string; message: string };

/**
 * Starts a BNSF Follow-Ups run: uploads the workbook, and the BFF holds the parsed rows and
 * the results accumulated so far in memory, keyed by the returned runId (nothing is persisted
 * -- a BFF restart loses the run, which is why the run store must not scale out). Throws, with
 * a message pulled from the BFF's body, for a bad file: not .xlsx, empty, no sheet with a
 * Voucher ID header, or more than 5,000 data rows.
 */
const startBnsfFollowUps = async (file: File, options: BnsfFollowUpsRunOptions): Promise<BnsfFollowUpsStartResult> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("includeVanCheck", String(options.includeVanCheck));
  formData.append("includeReopenNotes", String(options.includeReopenNotes));
  try {
    return await apiClient.post<FormData, BnsfFollowUpsStartResult>("/finance/bnsf-followups/start", formData);
  } catch (e: any) {
    // The BFF's 400 body is a plain BadRequest(string) -- e.g. "Only .xlsx workbooks are
    // supported." -- not an object, so check for that before falling back to the ProblemDetails
    // shape a model-binding rejection would use instead.
    const data = e?.response?.data;
    const message = typeof data === "string" ? data : data?.message || data?.title || data?.detail;
    throw new Error(message || "Upload failed. Check the file and try again.");
  }
};

/**
 * Runs one more batch of an in-progress run. Call this in a tight loop (no client-side delay
 * needed -- each call already does up to ~50s of real work) until isComplete, updating a
 * progress bar from processedRows/totalRows after every response.
 *
 * Never throws -- every failure this call can hit comes back as an outcome instead, so a batch
 * loop driven off this function can always show it inline with a retry/cancel choice rather than
 * crashing with an unhandled rejection: "expired" when the BFF no longer knows the runId (404 --
 * TTL eviction or a restart); "failed" with failedStep "permission" when OrderApi refuses the
 * batch call for AccessBnsfFollowUps (403, forwarded verbatim per criterion 2 -- the cursor is
 * untouched, so retrying resends the same batch once the permission is granted); "failed" naming
 * the batch when OrderApi never responded at all (502, e.g. the process was killed mid-run --
 * again cursor-untouched, so Retry resumes the same batch once it's back up); "failed" naming the
 * step for any other OrderApi-side failure (502); and a generic "failed" for anything else (a raw
 * 500, a network drop, a timeout) that isn't one of the above -- this last case used to rethrow
 * and become an unhandled promise rejection / CRA overlay in the batch loop (test.md Defect B).
 */
const nextBnsfFollowUpsBatch = async (runId: string): Promise<BnsfFollowUpsBatchOutcome> => {
  try {
    const result = await apiClient.post<undefined, BnsfFollowUpsBatchResult>(
      `/finance/bnsf-followups/${runId}/next-batch`
    );
    return { status: "ok", ...result };
  } catch (e: any) {
    const httpStatus = e?.response?.status;
    const data = e?.response?.data;
    if (httpStatus === 404) return { status: "expired" };
    if (httpStatus === 403) {
      // The BFF forwards OrderApi's 403 body verbatim -- a plain string naming the permission
      // (e.g. "AccessBnsfFollowUps"), not an object.
      const permissionName = typeof data === "string" ? data : data?.message;
      return {
        status: "failed",
        failedStep: "permission",
        message: permissionName
          ? `Missing permission: ${permissionName}. Ask an admin to grant it, then retry.`
          : "You do not have permission to run BNSF Follow-Ups.",
      };
    }
    if (httpStatus === 502) {
      return {
        status: "failed",
        failedStep: data?.failedStep ?? "unknown step",
        message: data?.message ?? "The batch failed and the run cannot continue.",
      };
    }
    // Anything else -- a raw 500, a network drop, a timeout, a proxy error with no JSON body at
    // all -- is still surfaced as a "failed" outcome rather than rethrown, so the batch loop
    // never has to catch an unexpected shape. The cursor on the BFF is untouched in every one of
    // these cases (next-batch never calls CommitBatch except on a successful OrderApi response),
    // so Retry always resends the same batch.
    const message = typeof data === "string" && data ? data : data?.message || e?.message || "The batch failed and the run cannot continue.";
    return { status: "failed", failedStep: "batch", message };
  }
};

/** Downloads the filled workbook once isComplete is true. Evicts the run from the BFF's store. */
const downloadBnsfFollowUps = async (runId: string): Promise<Blob> => {
  try {
    return await apiClient.getBlob<undefined, Blob>(`/finance/bnsf-followups/${runId}/download`);
  } catch (e) {
    throw new Error("Download failed. The run may have expired -- start again.");
  }
};

export interface BnsfFollowUpsLookupRequest {
  voucherId: string;
  voucherType?: string;
  comment?: string;
  includeVanCheck: boolean;
  includeReopenNotes: boolean;
}

/** Mirrors OrderApi's VoucherResult field-for-field -- see plan.md "OrderApi contracts". */
export interface BnsfFollowUpsVoucherResult {
  voucherId: string;
  rcxResponse?: string;
  fuel?: number;
  idle?: number;
  miles?: number;
  shift?: number;
  dashboardMiles?: number;
  milesIdlePer60Hrs?: number;
  fitsTripTime?: string;
  otpStatus?: string;
  driverEarlyMin?: number;
  tripNumber?: number;
  rcxProblemType?: string;
  rcxStatus?: string; // "SEND" | "REVIEW" | "NOT DUE"
  rcxEvidenceNote?: string;
  /** Vehicle.UnitNumber -- set only on a Van-Conflict row where GPS confidently found THIS voucher's own keyed vehicle wrong. */
  correctVehicle?: number;
}

/**
 * Checks a single voucher without the file round-trip (Brendon's ask, secondary to the upload
 * flow, which stays primary). Sent through the same OrderApi batch call as the upload flow, just
 * with a one-element voucher list -- the batch contract guarantees exactly one VoucherResult back,
 * so a short key's non-uniqueness (if OrderApi finds more than one plausible record) shows up as
 * that one result's REVIEW status and evidence text, not a list of candidates.
 *
 * Throws (with a message pulled from the BFF's body) for a missing voucher id, a permission
 * refusal, or an OrderApi-side failure -- same house pattern as startBnsfFollowUps.
 */
const lookupBnsfFollowUp = async (request: BnsfFollowUpsLookupRequest): Promise<BnsfFollowUpsVoucherResult> => {
  try {
    // OrderApi's VoucherRequest.VoucherType/Comment are non-nullable strings (default "", not
    // optional) -- model binding rejects a missing/omitted property as "required" even though the
    // value itself may legitimately be blank, so an optional field here must still cross the wire
    // as an empty string rather than being left out of the JSON body.
    const payload: BnsfFollowUpsLookupRequest = {
      voucherId: request.voucherId,
      voucherType: request.voucherType ?? "",
      comment: request.comment ?? "",
      includeVanCheck: request.includeVanCheck,
      includeReopenNotes: request.includeReopenNotes,
    };
    return await apiClient.post<BnsfFollowUpsLookupRequest, BnsfFollowUpsVoucherResult>(
      "/finance/bnsf-followups/lookup",
      payload
    );
  } catch (e: any) {
    const httpStatus = e?.response?.status;
    const data = e?.response?.data;
    if (httpStatus === 403) {
      // Forwarded verbatim from OrderApi -- a plain string naming the permission.
      const permissionName = typeof data === "string" ? data : data?.message;
      throw new Error(
        permissionName
          ? `Missing permission: ${permissionName}. Ask an admin to grant it, then retry.`
          : "You do not have permission to run BNSF Follow-Ups."
      );
    }
    const message =
      typeof data === "string" && data ? data : data?.message || e?.message || "Lookup failed. Check the voucher ID and try again.";
    throw new Error(message);
  }
};

// Both dispute endpoints answer failures with PLAIN TEXT, not ProblemDetails: 400 for a bad or
// missing file and for a header-guard failure, 403 (import only) whose whole body is the missing
// permission name, and 502 (import only) when OrderApi never answered. The object fallbacks at the
// end are belt and braces for a model-binding rejection, which would still use ProblemDetails.
// Shared by preview and import so the two can never report the same server message differently.
const disputeUploadMessage = (e: any, fallback: string): string => {
  const httpStatus = e?.response?.status;
  const data = e?.response?.data;
  if (httpStatus === 403) {
    // The body is the bare permission name. Shown verbatim, but inside a sentence that says what
    // to do with it -- "AccessBnsfRoadDisputes" on its own is not an error message. Same wording
    // as the Follow-Ups calls above.
    const permissionName = typeof data === "string" && data ? data : data?.message;
    return permissionName
      ? `Missing permission: ${permissionName}. Ask an admin to grant it, then retry.`
      : "You do not have permission to upload BNSF road disputes.";
  }
  if (typeof data === "string" && data) return data;
  return data?.message || data?.title || data?.detail || e?.message || fallback;
};

/**
 * Parses Heidi's monthly BNSF Road Disputes workbook and returns what WOULD be imported.
 * Writes nothing -- no staging row, no batch id. Throws with the BFF's own message for a file
 * the parser cannot read at all.
 */
const previewBnsfRoadDisputes = async (file: File): Promise<PreviewBnsfRoadDisputesResult> => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    return await apiClient.post<FormData, PreviewBnsfRoadDisputesResult>(
      "/finance/disputes/bnsf/road/preview",
      formData
    );
  } catch (e: any) {
    throw new Error(disputeUploadMessage(e, "Could not read the workbook. Check the file and try again."));
  }
};

/**
 * Commits the workbook to dbo.BNSFManualDisputeTrip and returns the batch id and inserted count.
 *
 * The SAME file is posted again rather than the rows the preview returned: the import endpoint
 * re-parses server-side by design, because these are disputed dollar amounts and the browser is
 * not allowed to supply the numbers that land in the table (CONTRACT.md section 3). Do not try to
 * post the preview's rows back -- the endpoint does not accept them.
 *
 * There is no idempotency key behind this call, so a second successful post inserts a second
 * batch. Callers must not be able to fire it twice for one file -- see BnsfRoadDisputes.tsx's
 * phase lock and its synchronous in-flight ref.
 */
const importBnsfRoadDisputes = async (file: File): Promise<ImportBnsfRoadDisputesResult> => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    return await apiClient.post<FormData, ImportBnsfRoadDisputesResult>(
      "/finance/disputes/bnsf/road/import",
      formData
    );
  } catch (e: any) {
    // Deliberately does NOT claim nothing was written: a timeout or a dropped connection after the
    // proc committed looks identical from here, and telling a biller "nothing was saved" when a
    // batch did land is how you get a duplicate dispute batch.
    throw new Error(disputeUploadMessage(e, "Import failed and the result is unknown."));
  }
};

const financeService = {
  importBnsfFuelRates,
  runPayrollDryRun,
  getPayrollTripStatuses,
  startBnsfFollowUps,
  nextBnsfFollowUpsBatch,
  downloadBnsfFollowUps,
  lookupBnsfFollowUp,
  previewBnsfRoadDisputes,
  importBnsfRoadDisputes,
};

export default financeService;
